import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import Icon from '@jimmyboys/liita-ui/dist/components/Icon'
import ModalOptions from '@jimmyboys/liita-ui/dist/components/ModalOptions'
import Skeleton from '@jimmyboys/liita-ui/dist/components/Skeleton'
import Tooltip from '@jimmyboys/liita-ui/dist/components/Tooltip'
import { useReload } from 'store'

import styles from './AppointmentCard.module.sass'

const AppointmentStatus = (props) => {
	const { data, labels } = props
	const { screenSize, i18n, showDialog } = useGlobal()
	const reload = useReload()

	const isExecutedAndSummary =
		data?.status?.value === 'executed' && data.$detailLevel === 'summary'

	const options = [
		{
			value: 'comment',
			text: i18n`Insert comment`,
			action: () => {
				showDialog(
					'CommentDialog',
					i18n`Insert comment`,
					{
						processId: data.processId,
						appointmentId: data?.id,
					},
					false,
					undefined,
					'center',
					'comment-dialog'
				)
					.then(() => {
						reload([])
					})
					.catch(() => null)
			},
		},
		{
			value: 'cancel',
			text: labels.nullify,
			action: () => {
				showDialog(
					'NullifyAppointmentDialog',
					labels.nullifyAppointment + ` #${data?.id}`,
					{
						processId: data.processId,
						appointmentId: data.id,
						totalRequisitions: data.requisitions?.length || 0,
						nullifyMessage: `${labels.nullifyMessagePart1} ${
							data.requisitions?.length
						} ${
							data.requisitions?.length === 1
								? labels.nullifyMessagePart2Singular
								: labels.nullifyMessagePart2Plural
						}`,
						nullifyDerivedAppointments: labels.nullifyMessagePart3,
					},
					false,
					undefined,
					'center',
					'nullify-appointment-dialog'
				)
					.then(() => {
						reload([
							`/clinical_processes/${data.processId}/appointments`,
						])
					})
					.catch(() => null)
			},
		},
	]

	return (
		<>
			<Tooltip text={data.status?.description || ''}>
				<div className={styles['icon-container']}>
					<Icon
						className={styles['status-icon']}
						width='18px'
						height='18px'
						name={data?.status?.icon}
					/>
				</div>
			</Tooltip>
			<span className={styles['status-circle']} />
			<div
				className={`${styles['status-container']} ${
					styles[data.status.value] || ''
				} ${data?.$highlight ? styles['highlighted'] : ''}`}
			>
				<div className={styles['status-header-container']}>
					<div className={styles['status-header']}>
						<div className={styles['status-header-date']}>
							<strong>
								{data.date.separatedDateFormat?.day ?? 'N/A'}
							</strong>
							<small>
								<strong>
									{data.date.separatedDateFormat?.month ??
										'N/A'}
								</strong>
							</small>
							<small>
								{data.date.separatedDateFormat?.year ?? 'N/A'}
							</small>
						</div>
						<div className={styles['status-content']}>
							<div className={styles['status-content-header']}>
								<h5>
									#{data.id} | {data.medicalSpecialty?.name} |{' '}
									{data?.doctor?.name}
								</h5>
							</div>
							<HStack
								className={
									styles['status-content-lower-header']
								}
								columns={
									screenSize === 'small'
										? data?.transport
											? [1, 2, 2]
											: [1, 2]
										: []
								}
							>
								<span>
									<Icon
										className={styles['text-icon']}
										name='executed'
										width='14px'
										height='14px'
									/>
									<small>{data?.date?.shortTimeFormat}</small>
								</span>
								{data?.transport ? (
									<span>
										<Icon
											className={styles['text-icon']}
											name='transport'
											width='14px'
											height='14px'
										/>
										<small>{data.transport}</small>
									</span>
								) : null}
								<span>
									<Icon
										className={styles['text-icon']}
										name='location'
										width='14px'
										height='14px'
									/>
									<small>{data?.establishment?.name}</small>
								</span>
							</HStack>
						</div>
					</div>
					{!isExecutedAndSummary && (
						<div className={styles['options']}>
							<ModalOptions
								options={options}
								title={labels.options}
								centerArrow
								fixedHeight='auto'
								alwaysAbove
								selectable={false}
								onSelect={(item) => {
									item && item.action && item.action()
								}}
							>
								<Icon name='options' />
							</ModalOptions>
						</div>
					)}
				</div>
				{isExecutedAndSummary && (
					<>
						<div className={styles['summary']}>
							<small className={styles['summary-content']}>
								{labels.disability}
								{': '}
								{data.disability?.rateStr
									? `${data.disability?.rateStr} - `
									: ''}
								{data.disability?.abbreviation}
								{data.currentDisability
									? ` / ${data.currentDisability.abbreviation}`
									: ''}
								{data.medicalObservation &&
									`, ${data.medicalObservation}`}
							</small>
						</div>
						<div
							className={`${styles['options']} ${styles['--executed-summary']}`}
						>
							<ModalOptions
								options={options}
								title={labels.options}
								centerArrow
								fixedHeight='auto'
								alwaysAbove
								selectable={false}
								onSelect={(item) => {
									item && item.action && item.action()
								}}
							>
								<Icon name='options' />
							</ModalOptions>
						</div>
					</>
				)}
			</div>
		</>
	)
}

export default AppointmentStatus

export const AppointmentStatusSkeleton = () => (
	<>
		<span className={styles['status-circle-skeleton']} />
		<div className={styles['status-container-skeleton']}>
			<div className={styles['status-header']}>
				<span className={styles['avatar-container']}>
					<Skeleton
						className={`${styles['skeleton']} ${styles['circle']}`}
					/>
				</span>
				<div className={styles['skeleton-status-content']}>
					<div>
						<h5>
							<Skeleton
								className={`${styles['skeleton']} ${styles['medium']}`}
							/>
						</h5>
						<small>
							<Skeleton className={styles['skeleton']} />
						</small>
					</div>
				</div>
			</div>
		</div>
	</>
)
